import React, { useEffect, useState } from "react";
import { Routes, Route, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError, money } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import ConciergeWidget from "@/components/ConciergeWidget";
import { PublicMenu } from "./PublicMenu";
import { SiteSections } from "@/components/site/SiteRenderer";
import { SiteHeader } from "@/components/site/SiteHeader";
import { buildDefaultSite, ensureFont, IMG } from "@/lib/siteDefaults";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Users, Eye, Wifi, Snowflake, Tv, Bath, Wind, Trees, CheckCircle2 } from "lucide-react";

const roomImg = (r, i = 0) => (r.photos && r.photos[0]) || (r.room_type === "APARTMENT" ? IMG.room2 : [IMG.room, IMG.room2][i % 2]);

function Section({ title, children, className = "" }) {
  return (
    <section className={`max-w-7xl mx-auto px-4 py-16 ${className}`}>
      {title && (<><h2 className="font-display text-4xl sm:text-5xl font-semibold text-forest-deep">{title}</h2>
        <div className="gold-rule w-24 my-5" /></>)}
      {children}
    </section>
  );
}

const amenityIcon = (a) => {
  const s = a.toLowerCase();
  if (s.includes("wi")) return <Wifi className="w-4 h-4" />;
  if (s.includes("климат") || s.includes("air")) return <Snowflake className="w-4 h-4" />;
  if (s.includes("тв") || s.includes("tv") || s.includes("телев")) return <Tv className="w-4 h-4" />;
  if (s.includes("сан") || s.includes("bath") || s.includes("баня")) return <Bath className="w-4 h-4" />;
  if (s.includes("сеш") || s.includes("dryer")) return <Wind className="w-4 h-4" />;
  return <Trees className="w-4 h-4" />;
};

function RoomCard({ r, onBook }) {
  const { t } = useLang();
  return (
    <div className="bg-white rounded-2xl overflow-hidden shadow-card border border-gold/10 group" data-testid="public-room-card">
      <div className="h-56 overflow-hidden">
        <img src={roomImg(r)} alt={r.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      </div>
      <div className="p-6">
        <div className="text-[10px] uppercase tracking-widest text-gold-dark">{r.room_type === "APARTMENT" ? t("sec_apartments") : t("nav_rooms")}</div>
        <h3 className="font-display text-2xl font-semibold text-forest-deep mt-1">{r.name}</h3>
        <div className="flex flex-wrap gap-4 mt-3 text-sm text-muted-foreground">
          <span className="flex items-center gap-1"><Eye className="w-4 h-4 text-gold-dark" /> {r.view}</span>
          <span className="flex items-center gap-1"><Users className="w-4 h-4 text-gold-dark" /> {r.max_adults} {t("adults").toLowerCase()}</span>
        </div>
        <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{r.description}</p>
        <div className="flex flex-wrap gap-2 mt-4">
          {(r.amenities || []).slice(0, 4).map((a) => (
            <span key={a} className="flex items-center gap-1 text-xs bg-cream-muted text-forest px-2 py-1 rounded-lg">{amenityIcon(a)} {a}</span>
          ))}
        </div>
        <div className="flex items-center justify-between mt-5 pt-5 border-t border-gold/10">
          <div>
            <div className="text-2xl font-bold text-forest-deep">{money(r.base_price)}</div>
            <div className="text-xs text-muted-foreground">{t("per_night")}</div>
          </div>
          <Button onClick={() => onBook(r)} data-testid="public-book-btn"
            className="bg-gold hover:bg-gold-dark text-forest-deep rounded-xl">{t("book_now")}</Button>
        </div>
      </div>
    </div>
  );
}

export function RoomsGrid({ onBook }) {
  const [rooms, setRooms] = useState([]);
  useEffect(() => { api.get("/public/rooms").then((r) => setRooms(r.data)).catch(() => {}); }, []);
  return (
    <section className="max-w-7xl mx-auto px-4 pb-16">
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {rooms.map((r) => <RoomCard key={r.id} r={r} onBook={onBook} />)}
      </div>
    </section>
  );
}

export function BookPage({ preselect, clearPreselect }) {
  const { t } = useLang();
  const today = new Date().toISOString().slice(0, 10);
  const tmr = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const [f, setF] = useState({ arrival: today, departure: tmr, adults: 2, children: 0, babies: 0 });
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bookRoom, setBookRoom] = useState(null);

  const search = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/public/availability", { params: f });
      setResults(data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  useEffect(() => { if (preselect) { setBookRoom(preselect); clearPreselect(); } }, [preselect, clearPreselect]);

  return (
    <Section title={t("nav_book")}>
      <div className="bg-white rounded-2xl shadow-card border border-gold/10 p-6 grid sm:grid-cols-2 lg:grid-cols-6 gap-4 items-end">
        <div><Label className="text-xs">{t("arrival")}</Label><Input type="date" value={f.arrival} min={today} onChange={(e) => setF({ ...f, arrival: e.target.value })} data-testid="avail-arrival" className="mt-1" /></div>
        <div><Label className="text-xs">{t("departure")}</Label><Input type="date" value={f.departure} min={f.arrival} onChange={(e) => setF({ ...f, departure: e.target.value })} data-testid="avail-departure" className="mt-1" /></div>
        <div><Label className="text-xs">{t("adults")}</Label><Input type="number" min={1} value={f.adults} onChange={(e) => setF({ ...f, adults: +e.target.value })} className="mt-1" /></div>
        <div><Label className="text-xs">{t("children")}</Label><Input type="number" min={0} value={f.children} onChange={(e) => setF({ ...f, children: +e.target.value })} className="mt-1" /></div>
        <div><Label className="text-xs">{t("babies")}</Label><Input type="number" min={0} value={f.babies} onChange={(e) => setF({ ...f, babies: +e.target.value })} className="mt-1" /></div>
        <Button onClick={search} disabled={busy} className="h-10 bg-forest hover:bg-forest-light text-white rounded-xl" data-testid="check-availability-btn">{t("check_availability")}</Button>
      </div>

      {results && (
        <div className="mt-10">
          {results.rooms.length === 0 ? (
            <p className="text-muted-foreground">{t("no_rooms")}</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {results.rooms.map((r) => (
                <div key={r.id} className="bg-white rounded-2xl overflow-hidden shadow-card border border-gold/10" data-testid="availability-room-card">
                  <img src={roomImg(r)} alt="" className="h-48 w-full object-cover" />
                  <div className="p-5">
                    <h3 className="font-display text-2xl text-forest-deep">{r.name}</h3>
                    <p className="text-sm text-muted-foreground">{r.view} · {r.max_adults} {t("adults").toLowerCase()}</p>
                    <div className="flex justify-between items-end mt-4">
                      <div>
                        <div className="text-xl font-bold text-forest-deep">{money(r.total_stay)}</div>
                        <div className="text-xs text-muted-foreground">{r.nights} {t("nights")} · {money(r.base_price)}/{t("per_night")}</div>
                      </div>
                      <Button onClick={() => setBookRoom({ ...r, ...f })} className="bg-gold hover:bg-gold-dark text-forest-deep rounded-xl" data-testid="select-room-btn">{t("book_now")}</Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <BookDialog room={bookRoom} search={f} onClose={() => setBookRoom(null)} />
    </Section>
  );
}

function BookDialog({ room, search, onClose }) {
  const { t } = useLang();
  const today = new Date().toISOString().slice(0, 10);
  const tmr = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const [g, setG] = useState({ first_name: "", last_name: "", phone: "", email: "", country: "", special_request: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  useEffect(() => { if (room) { setDone(null); setG({ first_name: "", last_name: "", phone: "", email: "", country: "", special_request: "" }); } }, [room]);
  if (!room) return null;
  const arrival = room.arrival || search?.arrival || today;
  const departure = room.departure || search?.departure || tmr;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/public/book", {
        ...g, room_id: room.id, arrival, departure,
        adults: room.adults || search?.adults || 2, children: room.children || 0, babies: room.babies || 0,
      });
      setDone(data);
      toast.success(t("booking_success"));
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!room} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-2xl text-forest-deep">{room.name}</DialogTitle></DialogHeader>
        {done ? (
          <div className="text-center py-6">
            <CheckCircle2 className="w-16 h-16 text-forest mx-auto" />
            <h3 className="font-display text-2xl mt-4 text-forest-deep">{t("booking_success")}</h3>
            <p className="text-muted-foreground mt-2">{done.room_name} · {done.nights} {t("nights")} · <b>{money(done.total_price)}</b></p>
            <Button onClick={onClose} className="mt-6 bg-forest text-white rounded-xl">OK</Button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{arrival} → {departure}</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">{t("first_name")}</Label><Input value={g.first_name} onChange={(e) => setG({ ...g, first_name: e.target.value })} data-testid="book-first-name" /></div>
              <div><Label className="text-xs">{t("last_name")}</Label><Input value={g.last_name} onChange={(e) => setG({ ...g, last_name: e.target.value })} data-testid="book-last-name" /></div>
              <div><Label className="text-xs">{t("phone")}</Label><Input value={g.phone} onChange={(e) => setG({ ...g, phone: e.target.value })} data-testid="book-phone" /></div>
              <div><Label className="text-xs">{t("email")}</Label><Input value={g.email} onChange={(e) => setG({ ...g, email: e.target.value })} /></div>
              <div className="col-span-2"><Label className="text-xs">{t("country")}</Label><Input value={g.country} onChange={(e) => setG({ ...g, country: e.target.value })} /></div>
              <div className="col-span-2"><Label className="text-xs">{t("special")}</Label><Input value={g.special_request} onChange={(e) => setG({ ...g, special_request: e.target.value })} /></div>
            </div>
            <Button onClick={submit} disabled={busy || !g.first_name || !g.last_name}
              className="w-full h-12 bg-gold hover:bg-gold-dark text-forest-deep rounded-xl mt-2" data-testid="confirm-booking-btn">
              {t("confirm_booking")}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const useDevice = () => {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => { const f = () => setW(window.innerWidth); window.addEventListener("resize", f); return () => window.removeEventListener("resize", f); }, []);
  return w < 768 ? "mobile" : w < 1024 ? "tablet" : "desktop";
};

function SitePage({ doc, dynamic, device, navigate }) {
  const { page = "home" } = useParams();
  const { lang } = useLang();
  const p = doc.pages[page] || doc.pages.home;
  return <SiteSections sections={p.sections} lang={lang} device={device} theme={doc.theme} dynamic={dynamic} navigate={navigate} />;
}

export function useSiteDoc() {
  const [params] = useSearchParams();
  const [doc, setDoc] = useState(null);
  const preview = params.get("preview") === "1";
  useEffect(() => {
    const req = preview ? api.get("/site/draft").then((r) => r.data.data) : api.get("/public/site").then((r) => r.data);
    req.catch(() => null).then((d) => setDoc(d || buildDefaultSite()));
  }, [preview]);
  return { doc, preview };
}

export default function PublicSite() {
  const [preselect, setPreselect] = useState(null);
  const nav = useNavigate();
  const device = useDevice();
  const { doc, preview } = useSiteDoc();
  const { lang } = useLang();
  const clearPreselect = React.useCallback(() => setPreselect(null), []);
  const navigate = (href) => { nav(preview ? `${href}?preview=1` : href); window.scrollTo(0, 0); };
  const onBook = (room) => { navigate("/site/book"); setTimeout(() => setPreselect(room), 50); };
  useEffect(() => { if (doc) { ensureFont(doc.theme.heading_font); ensureFont(doc.theme.body_font); } }, [doc]);
  if (!doc) return <div className="min-h-screen bg-cream" />;
  const dynamic = {
    rooms: <RoomsGrid onBook={onBook} />,
    menu: <PublicMenu key={lang} />,
    booking: <BookPage preselect={preselect} clearPreselect={clearPreselect} />,
  };
  return (
    <div className="min-h-screen" style={{ background: doc.theme.bg, color: doc.theme.text, fontFamily: `'${doc.theme.body_font}', sans-serif` }}>
      {preview && <div className="bg-amber-400 text-black text-center text-sm py-1" data-testid="preview-banner">ПРЕГЛЕД на черновата – това не е публикувано</div>}
      <SiteHeader header={doc.header} device={device} onNavigate={navigate} />
      <Routes>
        <Route index element={<SitePage doc={doc} dynamic={dynamic} device={device} navigate={navigate} />} />
        <Route path=":page" element={<SitePage doc={doc} dynamic={dynamic} device={device} navigate={navigate} />} />
      </Routes>
      <footer className="mt-20"><SiteSections sections={doc.footer.sections} lang={lang} device={device} theme={doc.theme} navigate={navigate} /></footer>
      <ConciergeWidget />
    </div>
  );
}

